import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Plus, Search, ChevronRight, Loader2, FlaskConical,
  Building2, CheckCircle2, PauseCircle, CalendarPlus, X,
  Download, Upload,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import {
  getDiagnosticLabs, getDiagnosticLabStats, getDiagnosticLabUpdatedByOptions, editDiagnosticLab,
  LAB_TYPE_OPTIONS, labelFor, formatDateTime, auditName,
  downloadLabBulkTemplate, bulkUploadLabs, getLabBulkUploadStatus, LAB_BULK_UPLOAD_ACCEPT,
} from "../services/diagnosticLabsService";
import { StatCard, SegmentedControl, DiagnosticTabs, getCurrentRole } from "../components/diagnostic/primitives";

const PAGE_SIZE = 50;

const BulkUploadModal = ({
  open, onClose, onPickFile, fileInputRef, onFileSelected,
  uploadLoading, cooldownRemaining, uploadResult, onDismissResult,
  uploadDocumentId, uploadStatus, statusCheckLoading, onCheckStatus,
}) => {
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Bulk Upload Labs</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-900 leading-relaxed">
            <strong>Bulk upload:</strong> required columns are Brand name, Lab type, Address line, City, State, Pincode.
            Labs are created <strong>inactive</strong> — configure operations and activate each one afterward.
            Max 500 rows / 5MB per file — split larger files into multiple uploads (rows beyond 500 are dropped, not processed).
            After uploading, we'll check the result once automatically — if it's still processing, use "Check again".
          </div>

          {uploadResult && !uploadResult.success && (
            <div className="p-4 rounded-xl border text-sm bg-red-50 border-red-200">
              <div className="flex items-start justify-between gap-3">
                <p className="text-red-700 font-medium flex-1">{uploadResult.error || "Bulk upload failed."}</p>
                <button
                  onClick={onDismissResult}
                  className="text-gray-400 hover:text-gray-600 text-lg leading-none"
                  aria-label="Dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {uploadResult?.success && uploadDocumentId && (
            <div>
              {!uploadStatus ? (
                <div className="p-4 rounded-xl border text-sm bg-blue-50 border-blue-200 flex items-center gap-2 text-blue-800">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Checking upload status...
                </div>
              ) : uploadStatus.error ? (
                <div className="p-4 rounded-xl border text-sm bg-red-50 border-red-200 flex items-start justify-between gap-3">
                  <p className="text-red-700 font-medium flex-1">{uploadStatus.error}</p>
                  <button
                    onClick={() => onCheckStatus(uploadDocumentId)}
                    disabled={statusCheckLoading}
                    className="text-red-700 underline text-sm font-medium disabled:opacity-50 whitespace-nowrap"
                  >
                    {statusCheckLoading ? "Checking..." : "Check again"}
                  </button>
                </div>
              ) : uploadStatus.processing ? (
                <div className="p-4 rounded-xl border text-sm bg-blue-50 border-blue-200 flex items-center justify-between gap-3">
                  <p className="text-blue-800 font-medium">Still processing…</p>
                  <button
                    onClick={() => onCheckStatus(uploadDocumentId)}
                    disabled={statusCheckLoading}
                    className="px-3 py-1.5 border border-blue-400 text-blue-700 rounded-md text-xs font-medium hover:bg-blue-100 disabled:opacity-50 whitespace-nowrap"
                  >
                    {statusCheckLoading ? "Checking..." : "Check again"}
                  </button>
                </div>
              ) : (
                <div className={`p-4 rounded-xl border text-sm ${uploadStatus.skipped_count > 0 ? "bg-amber-50 border-amber-200" : "bg-green-50 border-green-200"}`}>
                  <p className={uploadStatus.skipped_count > 0 ? "text-amber-900 font-medium" : "text-green-800 font-medium"}>
                    {uploadStatus.created_count ?? 0} lab{(uploadStatus.created_count ?? 0) !== 1 ? "s" : ""} created.
                    {" "}They're inactive — configure operations and activate each one.
                    {(uploadStatus.skipped_count ?? 0) > 0 && (
                      <span> {uploadStatus.skipped_count} row{uploadStatus.skipped_count !== 1 ? "s" : ""} skipped.</span>
                    )}
                  </p>
                  {(uploadStatus.dropped_count ?? 0) > 0 && (
                    <p className="mt-1 text-xs text-amber-800">
                      {uploadStatus.dropped_count} row{uploadStatus.dropped_count !== 1 ? "s" : ""} were dropped because the file exceeded the 500-row cap.
                    </p>
                  )}
                  {uploadStatus.error_file_url && (
                    <a
                      href={uploadStatus.error_file_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block mt-2 text-orange-700 hover:text-orange-900 underline text-sm"
                    >
                      Download error report
                    </a>
                  )}
                </div>
              )}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept={LAB_BULK_UPLOAD_ACCEPT}
            className="hidden"
            onChange={onFileSelected}
          />
          <button
            onClick={onPickFile}
            disabled={uploadLoading || cooldownRemaining > 0}
            className="w-full px-4 py-2.5 border border-orange-500 text-orange-600 rounded-xl text-sm font-medium hover:bg-orange-50 flex items-center justify-center gap-2 transition-all disabled:opacity-50"
          >
            <Upload className="w-4 h-4" />
            {uploadLoading
              ? "Uploading..."
              : cooldownRemaining > 0
                ? `Wait ${cooldownRemaining}s`
                : "Upload Excel"}
          </button>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
const isActive = (lab) => lab?.active !== false;

const KPI_CARDS = [
  { key: "total_labs", label: "Total Labs", icon: FlaskConical, accent: { bg: "bg-blue-50", fg: "text-blue-600", bar: "bg-blue-500" } },
  { key: "active_labs", label: "Active", icon: CheckCircle2, accent: { bg: "bg-emerald-50", fg: "text-emerald-600", bar: "bg-emerald-500" } },
  { key: "inactive_labs", label: "Inactive", icon: PauseCircle, accent: { bg: "bg-amber-50", fg: "text-amber-600", bar: "bg-amber-500" } },
  { key: "added_today", label: "Added Today", icon: CalendarPlus, accent: { bg: "bg-cyan-50", fg: "text-cyan-600", bar: "bg-cyan-500" } },
];

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];
// Drop unknown ?status= values (old bookmarks) — the API rejects them.
const statusFromParam = (v) => (STATUS_OPTIONS.some((o) => o.value === v) ? v : "");

// Scroll offset saved when a row is opened, keyed by the list URL it was opened from,
// so Back lands on the same rows rather than the top of the page.
const SCROLL_KEY = "diagnosticLabsList:scroll";

const DiagnosticLabsList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const currentRole = getCurrentRole();
  const canAccess = currentRole === "Ops" || currentRole === "SuperAdmin";

  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [page, setPage] = useState(() => Number(searchParams.get("page") || 1));
  const [searchInput, setSearchInput] = useState(() => searchParams.get("search") || "");
  const [searchFilter, setSearchFilter] = useState(() => searchParams.get("search") || "");
  const [cityInput, setCityInput] = useState(() => searchParams.get("city") || "");
  const [cityFilter, setCityFilter] = useState(() => searchParams.get("city") || "");
  const [stateInput, setStateInput] = useState(() => searchParams.get("state") || "");
  const [stateFilter, setStateFilter] = useState(() => searchParams.get("state") || "");
  const [pincodeInput, setPincodeInput] = useState(() => searchParams.get("pincode") || "");
  const [pincodeFilter, setPincodeFilter] = useState(() => searchParams.get("pincode") || "");
  const [labType, setLabType] = useState(() => searchParams.get("lab_type") || "");
  const [status, setStatus] = useState(() => statusFromParam(searchParams.get("status")));
  const [updatedBy, setUpdatedBy] = useState(() => searchParams.get("updated_by") || "");
  const [updatedByOptions, setUpdatedByOptions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, items: PAGE_SIZE, total: 0, total_pages: 0 });

  const [templateLoading, setTemplateLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadCooldownUntil, setUploadCooldownUntil] = useState(null);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);
  const [uploadDocumentId, setUploadDocumentId] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [statusCheckLoading, setStatusCheckLoading] = useState(false);
  const fileInputRef = useRef(null);
  const cooldownTimerRef = useRef(null);
  const autoCheckTimerRef = useRef(null);

  const debounceRef = useRef(null);

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const resp = await getDiagnosticLabStats(currentRole);
      setStats(resp?.msg || resp);
    } catch {
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  // Last-updaters shift whenever a lab is edited, so this refreshes with the stats.
  const loadUpdatedByOptions = async () => {
    try {
      const resp = await getDiagnosticLabUpdatedByOptions(currentRole);
      const items = resp?.msg?.items;
      setUpdatedByOptions(Array.isArray(items) ? items : []);
    } catch {
      setUpdatedByOptions([]);
    }
  };

  useEffect(() => {
    const p = new URLSearchParams();
    if (page > 1) p.set("page", String(page));
    if (searchFilter.trim()) p.set("search", searchFilter.trim());
    if (cityFilter.trim()) p.set("city", cityFilter.trim());
    if (stateFilter.trim()) p.set("state", stateFilter.trim());
    if (pincodeFilter.trim()) p.set("pincode", pincodeFilter.trim());
    if (labType) p.set("lab_type", labType);
    if (status) p.set("status", status);
    if (updatedBy) p.set("updated_by", updatedBy);
    setSearchParams(p, { replace: true });
  }, [page, searchFilter, cityFilter, stateFilter, pincodeFilter, labType, status, updatedBy, setSearchParams]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Inputs already match the applied filters (first render, or Back from a lab) —
    // nothing changed, so keep the page from the URL instead of resetting it to 1.
    if (searchInput.trim() === searchFilter && cityInput.trim() === cityFilter
      && stateInput.trim() === stateFilter && pincodeInput.trim() === pincodeFilter) return undefined;
    debounceRef.current = setTimeout(() => {
      setSearchFilter(searchInput.trim());
      setCityFilter(cityInput.trim());
      setStateFilter(stateInput.trim());
      setPincodeFilter(pincodeInput.trim());
      setPage(1);
    }, 600);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, cityInput, stateInput, pincodeInput]);

  // Restore the scroll offset once the rows it was measured against are back.
  useEffect(() => {
    if (loading || labs.length === 0) return;
    try {
      const saved = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || "null");
      if (saved) sessionStorage.removeItem(SCROLL_KEY);
      if (!saved || saved.search !== location.search) return;
      // Cards and table settle over a few frames; keep nudging until the page is tall enough.
      let frames = 0;
      const restore = () => {
        window.scrollTo(0, saved.y);
        if (Math.abs(window.scrollY - saved.y) > 2 && frames++ < 30) requestAnimationFrame(restore);
      };
      requestAnimationFrame(restore);
    } catch {
      // sessionStorage unavailable — Back still keeps page and filters via the URL.
    }
  }, [loading, labs, location.search]);

  const openLab = (path) => {
    try {
      sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ search: location.search, y: window.scrollY }));
    } catch {
      // ignore — scroll restore is a convenience
    }
    navigate(path, { state: { from: `${location.pathname}${location.search}` } });
  };

  useEffect(() => {
    if (authLoading || !isAuthenticated || !canAccess) return;
    loadStats();
    loadUpdatedByOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, canAccess]);

  useEffect(() => {
    if (authLoading || !isAuthenticated || !canAccess) return;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const resp = await getDiagnosticLabs(currentRole, {
          page, items: PAGE_SIZE, search: searchFilter, city: cityFilter,
          state: stateFilter, pincode: pincodeFilter, lab_type: labType, status, updated_by: updatedBy,
        });
        const items = resp?.msg?.items || [];
        const pg = resp?.msg?.pagination;
        setLabs(Array.isArray(items) ? items : []);
        if (pg) setPagination({ ...pg, total_pages: Math.ceil((pg.total || 0) / (pg.items || PAGE_SIZE)) });
      } catch (err) {
        setError(err.message || "Failed to load labs.");
        setLabs([]);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, canAccess, currentRole, page, searchFilter, cityFilter, stateFilter, pincodeFilter, labType, status, updatedBy]);

  const handleToggle = async (lab, e) => {
    e.stopPropagation();
    const next = !isActive(lab);
    setLabs((prev) => prev.map((l) => (l.id === lab.id ? { ...l, active: next } : l)));
    try {
      await editDiagnosticLab(currentRole, lab.id, { active: next });
      loadStats();
      loadUpdatedByOptions();
    } catch {
      setLabs((prev) => prev.map((l) => (l.id === lab.id ? { ...l, active: !next } : l)));
    }
  };

  const hasFilters = labType || cityFilter || stateFilter || pincodeFilter || searchFilter || status || updatedBy;
  const clearFilters = () => {
    setSearchInput(""); setCityInput(""); setStateInput(""); setPincodeInput("");
    setLabType(""); setStatus(""); setUpdatedBy(""); setPage(1);
  };


  // Cooldown tick — single setTimeout chain, not a poll of any server state.
  useEffect(() => {
    if (!uploadCooldownUntil) {
      setCooldownRemaining(0);
      return;
    }
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((uploadCooldownUntil - Date.now()) / 1000));
      setCooldownRemaining(remaining);
      if (remaining > 0) {
        cooldownTimerRef.current = setTimeout(tick, 1000);
      } else {
        setUploadCooldownUntil(null);
      }
    };
    tick();
    return () => {
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
    };
  }, [uploadCooldownUntil]);

  useEffect(() => () => {
    if (autoCheckTimerRef.current) clearTimeout(autoCheckTimerRef.current);
  }, []);

  const checkUploadStatus = async (documentId) => {
    setStatusCheckLoading(true);
    try {
      const resp = await getLabBulkUploadStatus(currentRole, documentId);
      setUploadStatus(resp?.msg || resp);
    } catch (err) {
      setUploadStatus({ error: err.message || "Failed to check upload status." });
    } finally {
      setStatusCheckLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    setTemplateLoading(true);
    try {
      const blob = await downloadLabBulkTemplate(currentRole);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "diagnostic_labs_bulk_upload_template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setUploadResult({ success: false, error: err.message || "Failed to download template." });
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    setUploadLoading(true);
    setUploadResult(null);
    setUploadStatus(null);
    setUploadDocumentId(null);
    if (autoCheckTimerRef.current) clearTimeout(autoCheckTimerRef.current);
    try {
      const resp = await bulkUploadLabs(currentRole, file);
      const documentId = resp?.document_id || resp?.msg?.document_id;
      setUploadResult({ success: true });
      setUploadCooldownUntil(Date.now() + 75000);
      if (documentId) {
        setUploadDocumentId(documentId);
        autoCheckTimerRef.current = setTimeout(() => checkUploadStatus(documentId), 6000);
      }
    } catch (err) {
      setUploadResult({ success: false, error: err.message || "Upload failed." });
    } finally {
      setUploadLoading(false);
    }
  };

  if (!canAccess) {
    return (
      <div className="flex justify-center items-center h-64 text-center">
        <div>
          <h2 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h2>
          <p className="text-gray-600">You do not have permission to access Diagnostic Labs.</p>
        </div>
      </div>
    );
  }

  const inputCls = "w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50";

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/subscriber-wallet")}
              className="p-2 text-gray-500 hover:text-gray-900 hover:bg-white rounded-lg border border-transparent hover:border-gray-200 transition-all"
              aria-label="Back to home"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 leading-tight">Diagnostic Labs</h1>
              <p className="text-sm text-gray-400">Onboard &amp; manage labs and chains</p>
            </div>
          </div>
          <div className="self-start sm:self-auto flex items-center gap-2">
            <button
              onClick={handleDownloadTemplate}
              disabled={templateLoading}
              className="px-3 py-2.5 border border-green-600 text-green-700 rounded-xl text-sm font-medium hover:bg-green-50 flex items-center gap-2 transition-all disabled:opacity-50"
              title="Download Excel template for bulk lab onboarding"
            >
              <Download className="w-4 h-4" />
              {templateLoading ? "Downloading..." : "Download Template"}
            </button>
            <button
              onClick={() => setBulkUploadModalOpen(true)}
              className="px-3 py-2.5 border border-orange-500 text-orange-600 rounded-xl text-sm font-medium hover:bg-orange-50 flex items-center gap-2 transition-all"
              title="Upload filled Excel/CSV to bulk-add labs"
            >
              <Upload className="w-4 h-4" />
              Upload Excel
            </button>
            <button
              onClick={() => openLab("/subscriber-wallet/labs/labs/new")}
              className="px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 shadow-sm flex items-center gap-2 transition-all"
            >
              <Plus className="w-4 h-4" /> Create Lab
            </button>
          </div>
        </div>

        <BulkUploadModal
          open={bulkUploadModalOpen}
          onClose={() => setBulkUploadModalOpen(false)}
          onPickFile={() => fileInputRef.current?.click()}
          fileInputRef={fileInputRef}
          onFileSelected={handleFileSelected}
          uploadLoading={uploadLoading}
          cooldownRemaining={cooldownRemaining}
          uploadResult={uploadResult}
          onDismissResult={() => setUploadResult(null)}
          uploadDocumentId={uploadDocumentId}
          uploadStatus={uploadStatus}
          statusCheckLoading={statusCheckLoading}
          onCheckStatus={checkUploadStatus}
        />

        {/* Module tabs */}
        <DiagnosticTabs active="labs" />

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {KPI_CARDS.map((c, i) => (
            <StatCard key={c.key} index={i} icon={c.icon} label={c.label} accent={c.accent}
              loading={statsLoading} value={stats?.[c.key] ?? 0} />
          ))}
        </div>

        {/* Smart filter bar (sticky) */}
        <div className="sticky top-4 z-20 bg-white rounded-2xl shadow-sm border border-gray-200 p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input className={inputCls} placeholder="Search brand or legal name" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
            </div>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input className={inputCls} placeholder="City" value={cityInput} onChange={(e) => setCityInput(e.target.value)} />
            </div>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input className={inputCls} placeholder="State" value={stateInput} onChange={(e) => setStateInput(e.target.value)} />
            </div>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input className={inputCls} inputMode="numeric" maxLength={6} placeholder="Pincode" value={pincodeInput}
                onChange={(e) => setPincodeInput(e.target.value.replace(/\D/g, ""))} />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-400">Lab Type</span>
              <SegmentedControl size="sm" value={labType} onChange={(v) => { setLabType(v); setPage(1); }}
                options={[{ value: "", label: "All" }, ...LAB_TYPE_OPTIONS]} />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-400">Status</span>
              <SegmentedControl size="sm" value={status} onChange={(v) => { setStatus(v); setPage(1); }}
                options={STATUS_OPTIONS} />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-400">Updated By</span>
              <select value={updatedBy} onChange={(e) => { setUpdatedBy(e.target.value); setPage(1); }}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs text-gray-700 bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option value="">All</option>
                {/* Keep a URL-selected value visible even before / without its option loading. */}
                {updatedBy && !updatedByOptions.some((o) => o.value === updatedBy) && (
                  <option value={updatedBy}>Selected user</option>
                )}
                {updatedByOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label} ({o.count})</option>
                ))}
              </select>
            </div>
            {hasFilters && (
              <button onClick={clearFilters} className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700">
                <X className="w-3.5 h-3.5" /> Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          {error && <div className="px-6 py-3 text-sm text-red-700 bg-red-50 border-b border-red-100">{error}</div>}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  {["Lab", "Chain", "Lab Type", "City", "Created", "Updated By", "Active", ""].map((h, i) => (
                    <th key={i} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {labs.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-16 text-center">
                      {loading ? (
                        <div className="flex flex-col items-center gap-2 text-gray-400">
                          <Loader2 className="w-6 h-6 animate-spin" /><span className="text-sm">Loading labs…</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-3 text-gray-400">
                          <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center">
                            <FlaskConical className="w-6 h-6 text-gray-300" />
                          </div>
                          <p className="text-sm font-medium text-gray-500">No labs found</p>
                          <button onClick={() => openLab("/subscriber-wallet/labs/labs/new")} className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                            + Onboard your first lab
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ) : (
                  labs.map((lab) => {
                    const active = isActive(lab);
                    return (
                      <tr key={lab.id} onClick={() => openLab(`/subscriber-wallet/labs/labs/${lab.id}`)}
                        className={`group hover:bg-blue-50/40 cursor-pointer transition-colors ${active ? "" : "bg-gray-50/50"}`}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-600 flex items-center justify-center font-semibold text-sm shrink-0">
                              {(lab.brand_name || "?").charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className={`text-sm font-medium truncate ${active ? "text-gray-900" : "text-gray-400"}`}>{lab.brand_name || "N/A"}</p>
                              <p className="text-xs text-gray-400 truncate">{lab.business_name || ""}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">{lab.chain?.brand_name || "—"}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{labelFor(LAB_TYPE_OPTIONS, lab.lab_type)}</td>
                        <td className="px-6 py-4 text-sm text-gray-500">{lab.city || "—"}</td>
                        <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                          {/* Date over time — keeps the column narrow so the table needs less sideways scroll. */}
                          {(() => {
                            const [date, time] = formatDateTime(lab.created_at).split(", ");
                            return (
                              <>
                                <div>{date}</div>
                                {time && <div className="text-xs text-gray-400">{time}</div>}
                              </>
                            );
                          })()}
                        </td>
                        {/* Matches the Hospital list's "Updated By" column. Sourced from
                            the history tables rather than a column on the row, so it also
                            catches edits to a lab's contacts, address and meta. */}
                        <td className="px-6 py-4 text-sm text-gray-500 truncate" title={lab.last_updated_by?.email || ""}>
                          {auditName(lab.last_updated_by)}
                        </td>
                        <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                          <button type="button" role="switch" aria-checked={active} onClick={(e) => handleToggle(lab, e)}
                            className="relative inline-flex focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-full">
                            <span className={`block w-10 h-5 rounded-full transition-colors ${active ? "bg-emerald-500" : "bg-gray-300"}`} />
                            <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${active ? "translate-x-5" : "translate-x-0"}`} />
                          </button>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors ml-auto" />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {pagination.total > 0 && (
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {(pagination.page - 1) * pagination.items + 1}–
                {Math.min(pagination.page * pagination.items, pagination.total)} of {pagination.total}
              </div>
              <div className="flex items-center gap-2">
                <button disabled={pagination.page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={`px-3 py-1.5 border rounded-lg text-sm transition-colors ${pagination.page === 1 ? "text-gray-300 border-gray-200 cursor-not-allowed" : "text-gray-700 hover:bg-gray-50 border-gray-300"}`}>Prev</button>
                <button disabled={pagination.page >= (pagination.total_pages || 1)} onClick={() => setPage((p) => Math.min(pagination.total_pages || 1, p + 1))}
                  className={`px-3 py-1.5 border rounded-lg text-sm transition-colors ${pagination.page >= (pagination.total_pages || 1) ? "text-gray-300 border-gray-200 cursor-not-allowed" : "text-gray-700 hover:bg-gray-50 border-gray-300"}`}>Next</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DiagnosticLabsList;
